import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classnames from 'classnames';
import { Language } from 'enums/types';
import * as style from './Social.module.scss';

interface Image {
	svg: SVGSVGElement | any;
	alt: string;
	description: string;
}

interface Link {
	image: Image;
	to: string;
}

interface Props {
	language: string;
}

const translations = defineMessages({
	facebook: {
		id: 'linkDescription.facebook',
		defaultMessage:
			"Facebook icon will lead you to Bankera's Facebook page where we share news and updates about our project.",
	},
	twitter: {
		id: 'linkDescription.twitter',
		defaultMessage:
			"By clicking on the Twitter icon, you will enter Bankera's Twitter where we post relevant business updates.",
	},
	linkedin: {
		id: 'linkDescription.linkedin',
		defaultMessage:
			"Click on the LinkedIn icon and you will be redirected to Bankera's LinkedIn page.",
	},
	instagram: {
		id: 'linkDescription.instagram',
		defaultMessage:
			"Instagram icon leads you to Bankera's Instagram. You can find relevant pictures of Bankera there.",
	},
	youtube: {
		id: 'linkDescription.youtube',
		defaultMessage:
			"Press on Youtube icon to find tutorials and videos about our business on Bankera's YouTube channel.",
	},
});

const Social = ({ language }: Props) => {
	const { formatMessage } = useIntl();

	const links = [
		{
			image: {
				svg: (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="21.561"
						height="21.561"
						viewBox="0 0 21.561 21.561"
					>
						<path
							className="a"
							d="M21.561-12.609a1.132,1.132,0,0,0-.361-.866,1.2,1.2,0,0,0-.842-.337H1.2a1.173,1.173,0,0,0-.866.337A1.173,1.173,0,0,0,0-12.609V6.545a1.2,1.2,0,0,0,.337.842,1.132,1.132,0,0,0,.866.361H11.5V-.626H8.711V-3.85H11.5V-6.257a4.194,4.194,0,0,1,1.155-3.176,4.2,4.2,0,0,1,3.032-1.107q1.492,0,2.5.1v2.936H16.459a1.668,1.668,0,0,0-1.3.433,1.789,1.789,0,0,0-.289,1.155V-3.85H18.1L17.663-.626H14.871V7.748h5.486a1.132,1.132,0,0,0,.866-.361,1.2,1.2,0,0,0,.337-.842Z"
							transform="translate(0 13.813)"
						/>
					</svg>
				),
				alt: 'Facebook',
				description: formatMessage(translations.facebook),
			},
			to: 'https://www.facebook.com/bankeracom/',
		},
		{
			image: {
				svg: (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="32"
						height="32"
						viewBox="0 0 32 32"
					>
						<defs>
							<clipPath id="a">
								<rect
									width="32"
									height="32"
									transform="translate(244 333)"
									fill="#18bdbd"
									stroke="#707070"
									strokeWidth="1"
								/>
							</clipPath>
						</defs>
						<g transform="translate(-244 -333)" clipPath="url(#a)">
							<path
								className="a"
								d="M45.327,48h3.609l-7.883,8.992,9.274,12.237h-7.26l-5.69-7.42-6.5,7.42H27.26l8.43-9.619L26.8,48h7.443l5.138,6.782ZM44.059,67.075h2l-12.9-17.034H31.007Z"
								transform="translate(221.437 290.386)"
							/>
						</g>
					</svg>
				),
				alt: 'Twitter',
				description: formatMessage(translations.twitter),
			},
			to: 'https://twitter.com/Bankeracom',
		},
		{
			image: {
				svg: (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="21.561"
						height="21.561"
						viewBox="0 0 21.561 21.561"
					>
						<path
							className="a"
							d="M20.021-13.812a1.48,1.48,0,0,1,1.083.457,1.48,1.48,0,0,1,.457,1.083V6.208a1.561,1.561,0,0,1-1.54,1.54H1.54A1.48,1.48,0,0,1,.457,7.291,1.48,1.48,0,0,1,0,6.208V-12.272a1.48,1.48,0,0,1,.457-1.083,1.48,1.48,0,0,1,1.083-.457ZM6.5,4.668h.048v-10.3H3.321v10.3ZM4.909-7.027A1.806,1.806,0,0,0,6.232-7.58a1.779,1.779,0,0,0,.553-1.3,1.779,1.779,0,0,0-.553-1.3,1.806,1.806,0,0,0-1.323-.553,1.728,1.728,0,0,0-1.3.553,1.819,1.819,0,0,0-.529,1.3,1.819,1.819,0,0,0,.529,1.3A1.728,1.728,0,0,0,4.909-7.027ZM18.481,4.668V-.963a7,7,0,0,0-.674-3.513,3.455,3.455,0,0,0-3.128-1.4,3.708,3.708,0,0,0-1.973.529,2.692,2.692,0,0,0-1.059,1.107H11.6v-1.4H8.518v10.3h3.176v-5.1a3.949,3.949,0,0,1,.337-1.829A1.7,1.7,0,0,1,13.62-3.08a1.379,1.379,0,0,1,1.444.914A5.1,5.1,0,0,1,15.3-.337V4.668Z"
							transform="translate(0 13.813)"
						/>
					</svg>
				),
				alt: 'LinkedIn',
				description: formatMessage(translations.linkedin),
			},
			to: 'https://www.linkedin.com/company/bankera/',
		},
		{
			image: {
				svg: (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="21.561"
						height="21.561"
						viewBox="0 0 21.561 21.561"
					>
						<path
							className="a"
							d="M10.78-8.567a5.386,5.386,0,0,1,2.767.746A5.551,5.551,0,0,1,15.569-5.8a5.386,5.386,0,0,1,.746,2.767,5.386,5.386,0,0,1-.746,2.767,5.551,5.551,0,0,1-2.021,2.021A5.386,5.386,0,0,1,10.78,2.5a5.386,5.386,0,0,1-2.767-.746A5.551,5.551,0,0,1,5.992-.265a5.386,5.386,0,0,1-.746-2.767A5.386,5.386,0,0,1,5.992-5.8,5.551,5.551,0,0,1,8.013-7.821,5.386,5.386,0,0,1,10.78-8.567Zm0,9.144A3.477,3.477,0,0,0,13.331-.481,3.477,3.477,0,0,0,14.39-3.032a3.477,3.477,0,0,0-1.059-2.551A3.477,3.477,0,0,0,10.78-6.642,3.477,3.477,0,0,0,8.23-5.583,3.477,3.477,0,0,0,7.171-3.032,3.477,3.477,0,0,0,8.23-.481,3.477,3.477,0,0,0,10.78.578Zm7.075-9.385a1.5,1.5,0,0,1-.409.914,1.172,1.172,0,0,1-.89.385,1.249,1.249,0,0,1-.914-.385,1.249,1.249,0,0,1-.385-.914,1.249,1.249,0,0,1,.385-.914,1.249,1.249,0,0,1,.914-.385,1.249,1.249,0,0,1,.914.385A1.249,1.249,0,0,1,17.855-8.807Zm3.658,1.3q.048,1.348.048,4.476t-.072,4.5A9.076,9.076,0,0,1,21.08,3.85a5.633,5.633,0,0,1-3.417,3.417,9.076,9.076,0,0,1-2.382.409q-1.372.072-4.5.072t-4.5-.072A7.859,7.859,0,0,1,3.9,7.219,5.13,5.13,0,0,1,1.8,5.944,5.609,5.609,0,0,1,.481,3.85,9.076,9.076,0,0,1,.072,1.468Q0,.1,0-3.032t.072-4.5A9.076,9.076,0,0,1,.481-9.914,5.609,5.609,0,0,1,1.8-12.008,5.609,5.609,0,0,1,3.9-13.331a9.076,9.076,0,0,1,2.382-.409q1.372-.072,4.5-.072t4.5.072a9.076,9.076,0,0,1,2.382.409,5.609,5.609,0,0,1,2.094,1.323A5.609,5.609,0,0,1,21.08-9.914,9.791,9.791,0,0,1,21.513-7.508ZM19.2,3.321a10.133,10.133,0,0,0,.385-2.6q.048-1.059.048-2.984V-3.8q0-1.973-.048-2.984a9.657,9.657,0,0,0-.385-2.6,3.473,3.473,0,0,0-2.069-2.069,9.657,9.657,0,0,0-2.6-.385q-1.059-.048-2.984-.048H10.01q-1.925,0-2.984.048a10.133,10.133,0,0,0-2.6.385A3.473,3.473,0,0,0,2.358-9.385a9.657,9.657,0,0,0-.385,2.6Q1.925-5.727,1.925-3.8v1.54q0,1.925.048,2.984a10.133,10.133,0,0,0,.385,2.6A3.648,3.648,0,0,0,4.428,5.39a10.133,10.133,0,0,0,2.6.385q1.059.048,2.984.048h1.54q1.973,0,2.984-.048a9.657,9.657,0,0,0,2.6-.385A3.648,3.648,0,0,0,19.2,3.321Z"
							transform="translate(0 13.813)"
						/>
					</svg>
				),
				alt: 'Instagram',
				description: formatMessage(translations.instagram),
			},
			to: 'https://www.instagram.com/bankera/',
		},
		{
			image: {
				svg: (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="27.988"
						height="20.014"
						viewBox="0 0 27.988 20.014"
					>
						<g transform="translate(0.5 0.5)">
							<path
								className="a"
								d="M13.494,19.014c-.084,0-8.464-.006-10.544-.567a3.391,3.391,0,0,1-2.386-2.4A35.735,35.735,0,0,1,0,9.507,35.735,35.735,0,0,1,.564,2.969,3.391,3.391,0,0,1,2.95.567C5.03.006,13.41,0,13.494,0s8.463.006,10.543.567a3.391,3.391,0,0,1,2.386,2.4,35.735,35.735,0,0,1,.564,6.538,35.735,35.735,0,0,1-.564,6.538,3.391,3.391,0,0,1-2.386,2.4C21.957,19.008,13.579,19.014,13.494,19.014Zm-3.209-13.4v8.025l7.054-4.012Z"
								transform="translate(0 0)"
							/>
						</g>
					</svg>
				),
				alt: 'Youtube',
				description: formatMessage(translations.youtube),
			},
			to: 'https://www.youtube.com/channel/UC3lEI3rq1W4fPFYPu9ZezFg',
		},
	];

	return (
		<div>
			<ul className={style.list}>
				{links.map((link: Link) => (
					<li
						className={classnames(
							style.image,
							language === Language.Arabic && style.rightForArabicLang,
						)}
						key={link.to}
					>
						<a
							href={link.to}
							key={link.image.alt}
							title={link.image.description}
							target="_blank"
							rel="noopener noreferrer"
						>
							{link.image.svg}
						</a>
					</li>
				))}
			</ul>
		</div>
	);
};

export default Social;
